import { getBackendUrl } from "@/lib/backend";
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Loader2, Sparkles, ShieldCheck, Coins } from "lucide-react";
import { loadRazorpay } from "@/lib/razorpay";
import { formatApiError } from "@/lib/apiError";

const API = `${getBackendUrl()}/api`;

/**
 * CustomTopUpDialog — Razorpay-backed custom credit top-up.
 *
 * Customers (typically Team/Enterprise leads) above the standard pack
 * sizes can bulk-buy credits at the flat custom rate (₹12/credit) with a
 * minimum of ₹5,000. The dialog hydrates `inr_per_credit` / min / max
 * from `/api/billing/custom-topup/params` so backend stays the source
 * of truth — no copy of numbers in JS.
 *
 * Flow:
 *   1. Open → fetch params (₹/credit, min, max)
 *   2. User types an amount → live `credits = floor(amount / rate)` preview
 *   3. Confirm → POST /custom-topup/order → open Razorpay checkout
 *   4. On success → POST /verify (reuses standard pack verify; the
 *      txn record has `credits` baked in by the order step)
 */
export default function CustomTopUpDialog({ open, onOpenChange, defaultAmount, onPaid }) {
  const [params, setParams] = useState(null);
  const [amount, setAmount] = useState("");
  const [loadingParams, setLoadingParams] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setAmount("");
      return;
    }
    setLoadingParams(true);
    axios.get(`${API}/billing/custom-topup/params`)
      .then((r) => {
        setParams(r.data);
        setAmount(String(defaultAmount || r.data?.min_amount_inr || ""));
      })
      .catch(() => toast.error("Could not load top-up settings."))
      .finally(() => setLoadingParams(false));
  }, [open, defaultAmount]);

  // Live credit preview — floor(amount / rate). Computed on every
  // keystroke so the user sees exactly what they'll get *before* paying.
  const credits = useMemo(() => {
    if (!params) return 0;
    const a = Number(amount) || 0;
    return Math.floor(a / params.inr_per_credit);
  }, [amount, params]);

  const tooLow = params && Number(amount) > 0 && Number(amount) < params.min_amount_inr;
  const tooHigh = params && Number(amount) > params.max_amount_inr;
  const canSubmit = !submitting && params && Number(amount) >= params.min_amount_inr && Number(amount) <= params.max_amount_inr;

  const checkout = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/billing/custom-topup/order`,
        { amount_inr: Number(amount) }, { withCredentials: true });
      const order = r.data;
      await loadRazorpay();
      if (!window.Razorpay) throw new Error("Razorpay not available.");

      // Hand the interaction layer to Razorpay — close our dialog first.
      onOpenChange(false);
      await new Promise((res) => setTimeout(res, 50));

      const rzp = new window.Razorpay({
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        order_id: order.order_id,
        name: order.name,
        description: order.description,
        prefill: order.prefill,
        notes: order.notes,
        // Disable Razorpay's cross-merchant "remember last contact" cookie
        // so a different signed-in user never sees the previous user's
        // phone number ("Using as +91 …") in our checkout.
        remember_customer: false,
        theme: { color: "#D4AF37" },
        modal: {
          ondismiss: () => {
            setSubmitting(false);
            toast.message("Checkout closed. No charge was made.");
          },
        },
        handler: async (resp) => {
          try {
            const v = await axios.post(`${API}/billing/verify`, {
              order_id: resp.razorpay_order_id,
              payment_id: resp.razorpay_payment_id,
              signature: resp.razorpay_signature,
            });
            if (v.data?.ok) {
              toast.success(`Payment confirmed · ${v.data.credits.toLocaleString("en-IN")} credits added.`);
              onPaid?.();
            } else {
              toast.error("Verification failed. Please contact support with payment id.");
            }
          } catch (err) {
            toast.error(formatApiError(err, "Verification failed."));
          } finally {
            setSubmitting(false);
          }
        },
      });
      rzp.on("payment.failed", (e) => {
        setSubmitting(false);
        toast.error(`Payment failed: ${e?.error?.description || "Please try again."}`);
      });
      rzp.open();
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401) {
        toast.error("Please sign in to purchase credits.");
        window.location.href = "/signin?next=/app";
        return;
      }
      toast.error(formatApiError(err, "Could not start checkout."));
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!submitting) onOpenChange(v); }}>
      <DialogContent
        data-testid="custom-topup-dialog"
        className="bg-[#0E0E0E] border border-white/10 text-white p-0 rounded-2xl shadow-[0_30px_80px_-30px_rgba(0,0,0,0.8)] w-[94vw] max-w-[460px] sm:!rounded-2xl"
      >
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-white/8 space-y-1">
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[#D4AF37]">
            <Sparkles className="w-3 h-3" /> Custom top-up
          </div>
          <DialogTitle className="font-display text-xl tracking-tight text-white">
            Buy any number of credits
          </DialogTitle>
          <DialogDescription className="text-white/50 text-[11px] leading-snug">
            {loadingParams || !params ? "Loading rate…" : (
              <>
                ₹{Number(params.inr_per_credit).toFixed(params.inr_per_credit % 1 === 0 ? 0 : 1)}
                {" "}per credit · minimum ₹{params.min_amount_inr.toLocaleString("en-IN")}.
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="px-5 py-4 space-y-4">
          <div>
            <label
              htmlFor="custom-topup-amount"
              className="text-[10px] uppercase tracking-[0.22em] text-white/45 block mb-1.5"
            >
              Amount you&apos;d like to spend
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/55 text-sm pointer-events-none">₹</span>
              <input
                id="custom-topup-amount"
                type="number"
                inputMode="numeric"
                step="100"
                min={params?.min_amount_inr || 5000}
                max={params?.max_amount_inr || 500000}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="20000"
                data-testid="custom-topup-amount-input"
                className="w-full bg-[#141414] border border-white/10 rounded-md pl-7 pr-3 py-2.5 text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37]"
              />
            </div>
            {tooLow && (
              <p className="text-[11px] text-rose-300 mt-1.5" data-testid="custom-topup-too-low">
                Minimum ₹{params.min_amount_inr.toLocaleString("en-IN")} for custom top-ups. Try a credit pack for smaller amounts.
              </p>
            )}
            {tooHigh && (
              <p className="text-[11px] text-rose-300 mt-1.5" data-testid="custom-topup-too-high">
                Single top-ups capped at ₹{params.max_amount_inr.toLocaleString("en-IN")}. Reach out to sales for higher.
              </p>
            )}
          </div>

          {/* Live preview */}
          <div className="rounded-xl border border-[#D4AF37]/30 bg-gradient-to-br from-[#D4AF37]/8 via-[#1A1306] to-[#0E0E0E] p-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37]">You&apos;ll receive</div>
                <div className="font-display text-3xl text-white tabular-nums mt-0.5" data-testid="custom-topup-credits-preview">
                  {credits.toLocaleString("en-IN")} <span className="text-base text-white/55">credits</span>
                </div>
              </div>
              <Coins className="w-7 h-7 text-[#D4AF37]" />
            </div>
            <div className="mt-2 text-[11px] text-white/45">
              At ₹{params ? Number(params.inr_per_credit).toFixed(params.inr_per_credit % 1 === 0 ? 0 : 1) : "8"} / credit · credits never expire
            </div>
          </div>

          <button
            type="button"
            onClick={checkout}
            disabled={!canSubmit}
            data-testid="custom-topup-checkout-btn"
            className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#D4AF37] text-black text-sm font-medium py-3 hover:bg-[#F3CE5A] transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {submitting
              ? "Starting checkout…"
              : `Pay ₹${(Number(amount) || 0).toLocaleString("en-IN")} via Razorpay`}
          </button>
          <p className="text-[10px] text-white/40 text-center leading-relaxed inline-flex items-center justify-center gap-1.5">
            <ShieldCheck className="w-3 h-3" /> Secured by Razorpay · UPI / Cards / NetBanking
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
